import GlobalNotification from '../../component/common/snackbar';
import i18n from '../../i18n';
import { ITEM_TYPE } from '../../models/menu-dish.entity';
import type {
    MenuStoreLicenseCodeRow,
    MenuStoreLicenseCodeStatus,
    MenuStoreListResponse,
    MenuStoreMenuListItem,
    MenuStoreMyLicenseActivation,
    MenuStorePatchDishVisibilityItem,
    MenuStoreTemplateMenuRow,
} from '../../models/menu-store.entity';

/**
 * 将接口里可能出现的日期标量归一为 ISO 字符串，供存入 `activated_at` 并由 `dateFormat` 展示。
 * @param raw - 字符串、数字时间戳、`Date` 或 Mongo 风格 `{ $date: ... }`
 * @returns ISO 字符串；无法识别时返回 `undefined`
 */
function coalesceUnknownDateToIsoString(raw: unknown): string | undefined {
    if (raw == null) {
        return undefined;
    }
    if (typeof raw === 'string') {
        /** 接口常见 ISO8601；空串视为无值。 */
        const t = raw.trim();
        return t.length > 0 ? t : undefined;
    }
    if (typeof raw === 'number' && Number.isFinite(raw)) {
        /** 大于约 2001-09-09 的秒级时间戳按毫秒处理，否则按秒乘 1000。 */
        const ms = raw > 1e12 || raw < -1e11 ? raw : raw * 1000;
        const d = new Date(ms);
        return Number.isNaN(d.getTime()) ? undefined : d.toISOString();
    }
    if (raw instanceof Date) {
        return Number.isNaN(raw.getTime()) ? undefined : raw.toISOString();
    }
    if (typeof raw === 'object' && raw !== null) {
        /** Mongo / EJSON 常见：`{ "$date": "..." }` 或 `{ "$date": 毫秒数 }`。 */
        const o = raw as Record<string, unknown>;
        if ('$date' in o) {
            return coalesceUnknownDateToIsoString(o.$date);
        }
    }
    return undefined;
}

/**
 * 从单条授权码 JSON 中解析激活时间，写入展示行 `activated_at`。
 * 若仍为空，常见原因：① 后端未返回该字段；② 历史数据激活时尚未落库 `activated_at`；③ 字段名不在兼容列表且形态非字符串。
 * @param record - 单条原始对象
 * @returns 归一化后的 ISO 时间串；无则 `undefined`
 */
function normalizeLicenseActivatedAtFromRecord(record: Record<string, unknown>): string | undefined {
    /** 与 dambuster `MenuStoreLicenseCode` 及网关别名对齐的候选键。 */
    const keys = [
        'activated_at',
        'activatedAt',
        'activation_time',
        'activated_time',
        'used_at',
        'usedAt',
        'redeemed_at',
    ];
    for (const key of keys) {
        const iso = coalesceUnknownDateToIsoString(record[key]);
        if (iso != null) {
            return iso;
        }
    }
    return undefined;
}

/**
 * 若成功体被再包一层（如 `data` / `result` 内才有 `list`），则向内剥离以便后续解析数组。
 * @param payload - HTTP 成功体
 * @returns 与 `unwrapLicenseCodeArray` 兼容的对象或原样返回
 */
function peelLicenseCodesResponsePayload(payload: unknown): unknown {
    if (payload == null || typeof payload !== 'object' || Array.isArray(payload)) {
        return payload;
    }
    const root = payload as Record<string, unknown>;
    /** 仅当内层明显是「含列表的菜单授权码响应」时才替换，避免误剥其它结构。 */
    const inner = (root.data ?? root.result ?? root.payload) as Record<string, unknown> | undefined;
    if (inner != null && typeof inner === 'object' && !Array.isArray(inner)) {
        const hasListShape =
            Array.isArray(inner.list) ||
            Array.isArray(inner.generated) ||
            Array.isArray(inner.license_codes) ||
            Array.isArray(inner.codes) ||
            Array.isArray(inner.items);
        if (hasListShape) {
            return inner;
        }
    }
    return payload;
}

/**
 * 解析发布人展示文案：优先姓名，其次联系方式。
 * @param item - 菜单卡片数据
 * @returns 发布人展示文本
 */
export function formatPublisher(item: MenuStoreMenuListItem): string {
    /** 发布人联系方式对象。 */
    const creatorContact = item.creator_contact;
    if (creatorContact?.name?.trim()) {
        return creatorContact.name.trim();
    }
    if (creatorContact?.contact_text?.trim()) {
        return creatorContact.contact_text.trim();
    }
    if (creatorContact?.email?.trim()) {
        return creatorContact.email.trim();
    }
    return i18n.t('menu_store_publisher_unknown');
}

/**
 * 将菜单时间格式化为 `DD/MM/YYYY`。
 * @param value - 原始时间值
 * @returns 格式化后日期
 */
export function formatMenuStoreDate(value: string | Date | undefined): string {
    if (value == null) {
        return '—';
    }
    /** 统一转 Date 方便判定与格式化。 */
    const dateValue = typeof value === 'string' ? new Date(value) : value;
    if (Number.isNaN(dateValue.getTime())) {
        return '—';
    }
    /** 年份数字。 */
    const year = dateValue.getFullYear();
    /** 月份数字（补零）。 */
    const month = String(dateValue.getMonth() + 1).padStart(2, '0');
    /** 日期数字（补零）。 */
    const day = String(dateValue.getDate()).padStart(2, '0');
    return `${day}/${month}/${year}`;
}

/**
 * 解析菜单封面图地址：优先预览图，其次原图。
 * @param item - 菜单卡片数据
 * @returns 可用于 CSS 背景图的 URL；无可用图时返回空字符串
 */
export function resolveMenuStoreCoverImage(item: MenuStoreMenuListItem): string {
    /** 预览图地址（通常更适合列表）。 */
    const previewUrl = normalizeMenuStoreImageUrl(item.cover_image_preview_url);
    if (previewUrl.length > 0) {
        return previewUrl;
    }
    /** 原图地址（预览图缺失时兜底）。 */
    const coverUrl = normalizeMenuStoreImageUrl(item.cover_image_url);
    if (coverUrl.length > 0) {
        return coverUrl;
    }
    return '';
}

/**
 * 解析菜品图片地址：优先详情图，其次图标图。
 * @param dish - 菜品行数据
 * @returns 可用图片 URL；无图时返回空字符串
 */
export function resolveMenuStoreDishImage(dish: MenuStoreTemplateMenuRow): string {
    /** 菜品详情图地址。 */
    const imageUrl = normalizeMenuStoreImageUrl(dish.image_url);
    if (imageUrl.length > 0) {
        return imageUrl;
    }
    /** 菜品图标地址。 */
    const iconUrl = normalizeMenuStoreImageUrl(dish.icon_url);
    if (iconUrl.length > 0) {
        return iconUrl;
    }
    /** 商店/模板接口常见小图 URL（与 `dish_image` 成对）。 */
    const dishSmall = normalizeMenuStoreImageUrl(dish.dish_image_small ?? undefined);
    if (dishSmall.length > 0) {
        return dishSmall;
    }
    /** 门店菜单接口常见 `dish_image`。 */
    const dishImg = normalizeMenuStoreImageUrl(dish.dish_image ?? undefined);
    if (dishImg.length > 0) {
        return dishImg;
    }
    const bigFood = normalizeMenuStoreImageUrl(dish.bigFoodImage);
    if (bigFood.length > 0) {
        return bigFood;
    }
    const food = normalizeMenuStoreImageUrl(dish.foodImage);
    if (food.length > 0) {
        return food;
    }
    return '';
}

/**
 * 规范化图片 URL：过滤非字符串与无效占位值，避免覆盖默认图兜底。
 * 云端同步模板偶见 `dish_image` / `dish_image_small` 为空对象 `{}`，不可对非字符串调用 `.trim`。
 * @param rawUrl - 原始图片地址（接口可能为 string / null / 对象）
 * @returns 可用 URL；无效则返回空字符串
 */
function normalizeMenuStoreImageUrl(rawUrl: unknown): string {
    // 仅接受字符串；对象 / 数字 / null / undefined 一律视为无图
    if (typeof rawUrl !== 'string') {
        return '';
    }
    /** 去掉两端空格后的地址值。 */
    const value = rawUrl.trim();
    if (!value) {
        return '';
    }
    /** 常见无效占位值集合。 */
    const invalidValues = new Set(['null', 'undefined', '-', '—', 'N/A', 'n/a']);
    if (invalidValues.has(value)) {
        return '';
    }
    return value;
}

/**
 * 扁平化结构体中的菜品列表，供详情右侧网格渲染。
 * @param item - 菜单详情数据
 * @returns 扁平菜品数组
 */
export function flattenDishesFromStructure(item: MenuStoreMenuListItem): MenuStoreTemplateMenuRow[] {
    /** 一级菜单块。 */
    const menuItem = item.structure?.menuItems?.[0];
    if (!menuItem?.secondData?.length) {
        return [];
    }
    /** 聚合后的扁平菜品数组。 */
    const result: MenuStoreTemplateMenuRow[] = [];
    for (const secondBlock of menuItem.secondData) {
        for (const menuRow of secondBlock.menuData ?? []) {
            result.push(menuRow);
        }
    }
    return result;
}

/**
 * 将 `my-menus` 返回中的单组列表段规范为 `MenuStoreListResponse`。
 * @param raw - 原始对象（如 `uploaded` / `purchased` 节点）
 * @returns 合法列表段；缺少 `list` 数组时返回 `null`
 */
function normalizeMenuStoreListSection(raw: unknown): MenuStoreListResponse | null {
    if (!raw || typeof raw !== 'object') {
        return null;
    }
    const o = raw as Record<string, unknown>;
    if (!Array.isArray(o.list)) {
        return null;
    }
    /** 当前页条数，用于补全缺省分页字段 */
    const len = o.list.length;
    return {
        list: o.list as MenuStoreMenuListItem[],
        total: typeof o.total === 'number' ? o.total : len,
        pagination_page: typeof o.pagination_page === 'number' ? o.pagination_page : 1,
        pagination_per_page: typeof o.pagination_per_page === 'number' ? o.pagination_per_page : len,
        page_total: typeof o.page_total === 'number' ? o.page_total : 1,
    };
}

/**
 * `GET /v3/menu-store/my-menus` 分组体（须同时含 **`uploaded`** 与 **`purchased`** 列表段）。
 * `purchased` 与历史别名 **`activated`** 二选一，优先 `purchased`。
 */
export type MenuStoreMyMenusGroupedParsed = {
    uploaded: MenuStoreListResponse;
    purchased: MenuStoreListResponse;
};

/**
 * 解析 `GET /v3/menu-store/my-menus` 成功体：仅接受分组响应（`uploaded` + `purchased`），不再兼容扁平单列表。
 * @param data - HTTP 成功体（未包装 `error`）
 * @returns 两侧列表段；缺少任一侧合法 `list` 数组时返回 `null`
 */
/**
 * 解析「我的菜单」单侧列表接口（`/my-menus/uploaded` | `/my-menus/purchased`）或标准列表体 `{ list, total }`。
 * @param data - HTTP 成功体（与 `GET .../menus` 列表段形态一致）
 */
export function parseMenuStoreListResponse(data: unknown): MenuStoreListResponse | null {
    if (data == null || typeof data !== 'object') {
        return null;
    }
    let raw: unknown = data;
    const top = data as Record<string, unknown>;
    if ('data' in top && top.data != null && typeof top.data === 'object' && !Array.isArray(top.data)) {
        raw = top.data;
    }
    return normalizeMenuStoreListSection(raw);
}

/**
 * 解析旧版 `GET /v3/menu-store/my-menus` 分组体（`uploaded` + `purchased`）；新前端已改走双侧独立接口，本函数仍保留供兼容或迁移期。
 */
export function parseMenuStoreMyMenusResponse(data: unknown): MenuStoreMyMenusGroupedParsed | null {
    if (!data || typeof data !== 'object') {
        return null;
    }
    const rec = data as Record<string, unknown>;
    /** 已购买/已激活侧列表段（兼容示例别名 `activated`） */
    const purchasedRaw = rec.purchased ?? rec.activated;
    const uploaded = normalizeMenuStoreListSection(rec.uploaded);
    const purchased = normalizeMenuStoreListSection(purchasedRaw);
    if (uploaded && purchased) {
        return { uploaded, purchased };
    }
    return null;
}

/**
 * 解析 `GET /v3/menu-store/menu/{id}` 成功体为列表项形态（兼容一层 `data` 包装与 `_id` / `id`）。
 * @param data - HTTP 成功体
 * @returns 可合并进 `selectedMenu` 的对象；无法得到合法 `_id` 时返回 `null`
 */
export function parseMenuStoreMenuDetailResponse(data: unknown): MenuStoreMenuListItem | null {
    if (data == null || typeof data !== 'object') {
        return null;
    }
    let raw = data as Record<string, unknown>;
    if ('data' in raw && raw.data != null && typeof raw.data === 'object' && !Array.isArray(raw.data)) {
        raw = raw.data as Record<string, unknown>;
    }
    const idRaw = raw._id ?? raw.id;
    if (idRaw == null) {
        return null;
    }
    /** Mongo 或字符串型 id */
    const _id = typeof idRaw === 'string' ? idRaw.trim() : String(idRaw);
    if (_id === '') {
        return null;
    }
    const name = typeof raw.name === 'string' ? raw.name : '';
    return { ...raw, _id, name } as MenuStoreMenuListItem;
}

/**
 * 将详情接口结果与列表项合并：详情字段优先，**固定**列表项传入的 `_id`；详情无名称时回退列表名称。
 * @param listItem - 进入详情前卡片上的数据
 * @param detail - `parseMenuStoreMenuDetailResponse` 结果
 * @returns 用于详情展示与后续 PATCH 的合并项
 */
export function mergeMenuStoreListItemWithDetail(
    listItem: MenuStoreMenuListItem,
    detail: MenuStoreMenuListItem
): MenuStoreMenuListItem {
    return {
        ...listItem,
        ...detail,
        _id: listItem._id,
        name: detail.name?.trim() ? detail.name : listItem.name,
    };
}

/**
 * 编辑预览打码态在组件内的稳定键（与 `collectMenuStoreDishVisibilityPatches` 一致）。
 * @param secondIdx - 二级分类索引；无分类时用 `-1`
 * @param dishIdx - 分类内菜品下标
 * @param row - 菜品行
 * @returns 映射键
 */
export function menuStoreDishMaskKey(secondIdx: number, dishIdx: number, row: MenuStoreTemplateMenuRow): string {
    return `${secondIdx}-${dishIdx}-${row.item_name1 ?? ''}-${row.nlu ?? ''}`;
}

/**
 * 解析 `dish-visibility` 所需的 `dish_template_item_id`（兼容 structure 中的多种字段名）。
 * @param row - 模板菜品行
 * @returns 可提交给后端的 id；无法解析则 `undefined`
 */
export function resolveMenuStoreDishTemplateItemId(row: MenuStoreTemplateMenuRow): string | undefined {
    const record = row as Record<string, unknown>;
    /** 读取字符串型 id */
    const asTrimmedString = (v: unknown): string | undefined =>
        typeof v === 'string' && v.trim() !== '' ? v.trim() : undefined;
    const fromExplicit =
        asTrimmedString(record.dish_template_item_id) ?? asTrimmedString(record.template_item_id);
    if (fromExplicit) {
        return fromExplicit;
    }
    if (row.item_id != null && Number.isFinite(Number(row.item_id))) {
        return String(row.item_id);
    }
    if (row.menu_item_id != null && Number.isFinite(Number(row.menu_item_id))) {
        return String(row.menu_item_id);
    }
    return undefined;
}

/**
 * 只读态下是否展示打码（无 `is_detail_visible` 时默认打码，与历史产品默认一致）。
 * @param row - 模板菜品行
 * @returns `true` 表示应打码
 */
export function isMenuStoreDishMaskedInStore(row: MenuStoreTemplateMenuRow): boolean {
    if (typeof row.is_detail_visible === 'boolean') {
        return !row.is_detail_visible;
    }
    return true;
}

/** 阿里云 OSS 图片模糊参数（未激活且不开放时拼在 `dish_image` 等 URL 后） */
const MENU_STORE_OSS_IMAGE_BLUR_QUERY = 'x-oss-process=image/blur,r_20,s_20';

/**
 * 在可识别的 http(s) 图片 URL 上追加 OSS 模糊处理参数（已含 `x-oss-process` 时不重复追加）。
 * @param imageUrl - 原始图片地址
 * @returns 追加参数后的地址；非 http(s) 或空串时原样返回
 */
export function appendMenuStoreOssImageBlurProcess(imageUrl: string): string {
    /** 去掉首尾空格后的地址 */
    const trimmed = (imageUrl ?? '').trim();
    if (!trimmed) {
        return trimmed;
    }
    if (!/^https?:\/\//i.test(trimmed)) {
        return trimmed;
    }
    if (trimmed.includes('x-oss-process=')) {
        return trimmed;
    }
    return trimmed.includes('?') ? `${trimmed}&${MENU_STORE_OSS_IMAGE_BLUR_QUERY}` : `${trimmed}?${MENU_STORE_OSS_IMAGE_BLUR_QUERY}`;
}

/**
 * 菜单详情页菜品列表瓦片用图：仅 **商店浏览（未激活）** 按 `is_detail_visible` 区分。
 * 不开放 → OSS 打码图；已开放 → 与 `resolveMenuStoreDishImage` 一致的原图。已激活 / 我上传不走本函数逻辑。
 * @param dishRow - 模板菜品行
 * @param detailSource - 详情来源
 * @returns 用于 CSS `background-image` 的图片 URL（无图时仍为空串）
 */
export function resolveMenuStoreBrowseDishTileImageUrl(
    dishRow: MenuStoreTemplateMenuRow,
    detailSource: 'store-home' | 'my-activated' | 'my-uploaded',
): string {
    const raw = resolveMenuStoreDishImage(dishRow);
    if (detailSource !== 'store-home' || !raw) {
        return raw;
    }
    if (isMenuStoreDishMaskedInStore(dishRow)) {
        return appendMenuStoreOssImageBlurProcess(raw);
    }
    return raw;
}

/**
 * 解析模板行上的多选键列表（过敏源、其他标识等）：支持 `string[]` 或 `*` 拼接字符串。
 * @param raw - 接口原始字段
 * @returns 非空键列表
 */
export function parseMenuStoreTemplateMultiKeys(raw: unknown): string[] {
    if (Array.isArray(raw)) {
        return raw.filter((x): x is string => typeof x === 'string' && x.length > 0);
    }
    if (typeof raw === 'string' && raw.trim()) {
        return raw.split('*').filter(Boolean);
    }
    return [];
}

/** 菜品详情弹窗整体形态：全量（已激活/我上传）或浏览态（商店未激活） */
export type MenuStoreDishDetailPageKind = 'full' | 'browse';

/**
 * 根据进入详情的来源解析弹窗应使用的形态。
 * @param detailSource - 详情页 `detailSource`
 */
export function resolveMenuStoreDishDetailPageKind(
    detailSource: 'store-home' | 'my-activated' | 'my-uploaded',
): MenuStoreDishDetailPageKind {
    return detailSource === 'store-home' ? 'browse' : 'full';
}

/** 浏览态下是否对描述/过敏源/其他标识做「需激活」占位 */
export type MenuStoreBrowseDishDetailVariant = 'locked' | 'open';

/**
 * 浏览态菜品详情子形态：`locked` = 不开放（图模糊 + 占位文案），`open` = 开放（展示真实字段）。
 * @param row - 模板菜品行
 */
export function resolveMenuStoreBrowseDishDetailVariant(row: MenuStoreTemplateMenuRow): MenuStoreBrowseDishDetailVariant {
    return isMenuStoreDishMaskedInStore(row) ? 'locked' : 'open';
}

/**
 * 浏览态详情顶区「菜品类型」展示文案：优先 `item_type`，否则 `class_id`，再兜底普通菜品。
 * @param row - 模板菜品行
 */
export function formatMenuStoreBrowseDishTypeLabel(row: MenuStoreTemplateMenuRow): string {
    const extra = row as MenuStoreTemplateMenuRow & Record<string, unknown>;
    /** 与门店菜品一致的类型数值（模板行可能只带其一） */
    let typeNum: number | undefined;
    if (typeof extra.item_type === 'number' && Number.isFinite(extra.item_type)) {
        typeNum = extra.item_type;
    } else if (row.class_id != null && Number.isFinite(Number(row.class_id))) {
        typeNum = Number(row.class_id);
    }
    if (typeNum === undefined) {
        return i18n.t('menu_library_add_dish_type_normal');
    }
    const hit = ITEM_TYPE.find((t) => t.value === typeNum);
    return hit?.label ?? i18n.t('menu_library_add_dish_type_normal');
}

/**
 * 进入编辑预览时的初值：有 `is_detail_visible` 则与只读态一致，否则默认打码。
 * @param item - 当前菜单详情
 * @returns `previewMaskMap`
 */
export function buildDefaultMenuStorePreviewMaskMap(item: MenuStoreMenuListItem): Record<string, boolean> {
    /** 打码映射：true 表示打码展示 */
    const map: Record<string, boolean> = {};
    /** 写入一行初值：优先用后端 `is_detail_visible` */
    const setEntry = (secondIdx: number, dishIdx: number, dishRow: MenuStoreTemplateMenuRow) => {
        const key = menuStoreDishMaskKey(secondIdx, dishIdx, dishRow);
        map[key] = isMenuStoreDishMaskedInStore(dishRow);
    };
    const secondBlocks = item.structure?.menuItems?.[0]?.secondData ?? [];
    if (secondBlocks.length > 0) {
        secondBlocks.forEach((block, secondIdx) => {
            (block.menuData ?? []).forEach((dishRow, dishIdx) => setEntry(secondIdx, dishIdx, dishRow));
        });
    } else {
        const flat = flattenDishesFromStructure(item);
        flat.forEach((dishRow, dishIdx) => setEntry(-1, dishIdx, dishRow));
    }
    return map;
}

/**
 * 根据当前打码映射组装 `dish-visibility` 单次 PATCH 的 `items` 列表。
 * @param item - 菜单详情
 * @param maskMap - `true` 打码 → `is_detail_visible: false`
 * @returns `items` 元素数组；缺 id 的菜品会被跳过
 */
export function collectMenuStoreDishVisibilityPatches(
    item: MenuStoreMenuListItem,
    maskMap: Record<string, boolean>
): MenuStorePatchDishVisibilityItem[] {
    /** 待提交的批量 items */
    const patches: MenuStorePatchDishVisibilityItem[] = [];
    /** 写入一条可见性 PATCH */
    const pushPatch = (secondIdx: number, dishIdx: number, dishRow: MenuStoreTemplateMenuRow) => {
        const key = menuStoreDishMaskKey(secondIdx, dishIdx, dishRow);
        /** 未出现在映射中时按默认打码处理 */
        const masked = maskMap[key] !== false;
        const dish_template_item_id = resolveMenuStoreDishTemplateItemId(dishRow);
        if (!dish_template_item_id) {
            return;
        }
        patches.push({ dish_template_item_id, is_detail_visible: !masked });
    };
    const secondBlocks = item.structure?.menuItems?.[0]?.secondData ?? [];
    if (secondBlocks.length > 0) {
        secondBlocks.forEach((block, secondIdx) => {
            (block.menuData ?? []).forEach((dishRow, dishIdx) => pushPatch(secondIdx, dishIdx, dishRow));
        });
    } else {
        const flat = flattenDishesFromStructure(item);
        flat.forEach((dishRow, dishIdx) => pushPatch(-1, dishIdx, dishRow));
    }
    return patches;
}

/**
 * 统计 structure 中菜品总数（与 `collectMenuStoreDishVisibilityPatches` 遍历范围一致）。
 * @param item - 菜单详情
 * @returns 菜品行数量
 */
export function countMenuStoreStructureDishes(item: MenuStoreMenuListItem): number {
    const secondBlocks = item.structure?.menuItems?.[0]?.secondData ?? [];
    if (secondBlocks.length > 0) {
        return secondBlocks.reduce((acc, block) => acc + (block.menuData?.length ?? 0), 0);
    }
    return flattenDishesFromStructure(item).length;
}

/**
 * 生成“联系我们”提示文本，多字段按换行拼接。
 * @param item - 菜单卡片数据
 * @returns 联系信息文本
 */
export function contactLines(item: MenuStoreMenuListItem): string {
    /** 发布人联系方式对象。 */
    const creatorContact = item.creator_contact;
    /** 联系方式分段数组。 */
    const contactParts = [
        creatorContact?.name,
        creatorContact?.phone_prefix && creatorContact?.phone ? `${creatorContact.phone_prefix} ${creatorContact.phone}` : creatorContact?.phone,
        creatorContact?.email,
        creatorContact?.contact_text,
    ].filter(Boolean) as string[];
    return contactParts.length > 0 ? contactParts.join('\n') : i18n.t('menu_store_contact_empty');
}

/**
 * 从对象中按候选 key 取非空字符串。
 * @param record - 响应对象
 * @param keys - 候选字段名
 * @returns 首个有效字符串或空串
 */
function pickNonEmptyString(record: Record<string, unknown>, keys: string[]): string {
    for (const key of keys) {
        const value = record[key];
        if (typeof value === 'string' && value.trim() !== '') {
            return value.trim();
        }
    }
    return '';
}

/**
 * 将 Apifox 未声明 schema 的列表响应归一为数组（兼容 `list` / `data` / 根数组等）。
 * @param payload - HTTP 成功体
 * @returns 元素列表
 */
function unwrapLicenseCodeArray(payload: unknown): unknown[] {
    const peeled = peelLicenseCodesResponsePayload(payload);
    if (Array.isArray(peeled)) {
        return peeled;
    }
    if (peeled != null && typeof peeled === 'object') {
        const record = peeled as Record<string, unknown>;
        /** Apifox 成功示例：`{ menu_id, generated: [{ code, status, created_at }] }`；dambuster GET 为 `{ menu_id, list }`。 */
        for (const key of ['generated', 'list', 'data', 'license_codes', 'codes', 'items']) {
            const value = record[key];
            if (Array.isArray(value)) {
                return value;
            }
        }
    }
    return [];
}

/**
 * 将单条授权码记录解析为展示行（字段名随后端可能变化）。
 * @param element - 单条原始元素
 * @returns 展示行；无法解析时返回 null
 */
function parseSingleLicenseRow(element: unknown): MenuStoreLicenseCodeRow | null {
    if (element == null || typeof element !== 'object') {
        return null;
    }
    const record = element as Record<string, unknown>;
    const code = pickNonEmptyString(record, ['license_code', 'code', 'licenseCode', 'value']);
    if (!code) {
        return null;
    }
    const statusRaw = pickNonEmptyString(record, ['status']).toLowerCase();
    /** Apifox：activated 已使用；其余按待使用处理 */
    const status: MenuStoreLicenseCodeStatus = statusRaw === 'activated' ? 'activated' : 'pending';
    /** 激活时间：字符串 / 时间戳 / `$date` 等见 `normalizeLicenseActivatedAtFromRecord`。 */
    const activatedAt = normalizeLicenseActivatedAtFromRecord(record);
    return { code, status, ...(activatedAt != null ? { activated_at: activatedAt } : {}) };
}

/**
 * 解析 `GET /v3/menu-store/menu/{id}/license-codes` 响应为列表行（OAS 无 schema，做多形态兼容）。
 * @param payload - HTTP 成功体
 * @returns 授权码列表
 */
export function parseMenuStoreLicenseCodesResponse(payload: unknown): MenuStoreLicenseCodeRow[] {
    const rawList = unwrapLicenseCodeArray(payload);
    /** 归一化后的展示行 */
    const rows: MenuStoreLicenseCodeRow[] = [];
    for (const el of rawList) {
        const row = parseSingleLicenseRow(el);
        if (row != null) {
            rows.push(row);
        }
    }
    return rows;
}

/**
 * 将授权码状态枚举映射为当前语言下的列表展示文案。
 * @param status - `pending` | `activated`
 * @returns 展示用状态文案
 */
export function formatMenuStoreLicenseStatus(status: MenuStoreLicenseCodeStatus): string {
    return status === 'activated'
        ? i18n.t('menu_store_license_status_activated')
        : i18n.t('menu_store_license_status_pending');
}

/**
 * 从 `POST .../license-codes` 成功体解析为列表行（典型：`{ menu_id, generated: [{ code, status, created_at }] }`），用于本地合并 `licenseCodeRows` 而无需再 GET。
 * @param payload - HTTP 成功体
 * @returns 新生成的展示行；无法解析结构化数组时返回空数组
 */
export function parseMenuStoreCreateLicenseCodeRows(payload: unknown): MenuStoreLicenseCodeRow[] {
    if (payload == null) {
        return [];
    }
    if (Array.isArray(payload)) {
        return parseMenuStoreLicenseCodesResponse(payload);
    }
    if (typeof payload === 'object') {
        const record = payload as Record<string, unknown>;
        const generated = record.generated;
        if (Array.isArray(generated) && generated.length > 0) {
            /** 优先使用 Apifox 约定的 `generated` 数组 */
            const rows: MenuStoreLicenseCodeRow[] = [];
            for (const el of generated) {
                const row = parseSingleLicenseRow(el);
                if (row != null) {
                    rows.push(row);
                }
            }
            if (rows.length > 0) {
                return rows;
            }
        }
        for (const key of ['license_codes', 'codes', 'list', 'data', 'items']) {
            const nested = record[key];
            if (!Array.isArray(nested)) {
                continue;
            }
            const fromNested = parseMenuStoreLicenseCodesResponse(nested);
            if (fromNested.length > 0) {
                return fromNested;
            }
        }
        /** 无 `generated` 数组时，整段对象可能即单条记录 */
        if (generated === undefined) {
            const row = parseSingleLicenseRow(record);
            return row != null ? [row] : [];
        }
    }
    return [];
}

/**
 * 从 `POST .../license-codes` 响应中解析新生成的码（Apifox 成功体含 `menu_id` + `generated[]`，每项含 `code`；仍兼容旧字段名）。
 * @param payload - HTTP 成功体
 * @returns 新生成的码列表（可能为空，由调用方提示）
 */
export function parseMenuStoreCreateLicenseCodesResponse(payload: unknown): string[] {
    if (payload == null) {
        return [];
    }
    if (typeof payload === 'string' && payload.trim() !== '') {
        return [payload.trim()];
    }
    if (Array.isArray(payload)) {
        /** 字符串数组 */
        const asStrings = payload.filter((x): x is string => typeof x === 'string' && x.trim() !== '').map((x) => x.trim());
        if (asStrings.length > 0) {
            return asStrings;
        }
        /** 对象数组，取 code 字段 */
        return parseMenuStoreLicenseCodesResponse(payload).map((r) => r.code);
    }
    if (typeof payload === 'object') {
        const record = payload as Record<string, unknown>;
        const single = pickNonEmptyString(record, ['license_code', 'code']);
        if (single) {
            return [single];
        }
        for (const key of ['generated', 'license_codes', 'codes', 'list', 'data', 'items']) {
            const nested = record[key];
            const fromNested = parseMenuStoreCreateLicenseCodesResponse(nested);
            if (fromNested.length > 0) {
                return fromNested;
            }
        }
    }
    return [];
}

/**
 * 从 `my_license_activations` 中选取用于展示的一条：优先 `activated_at` 最新；无有效时间则退回数组首项。
 * @param list - 详情接口返回的当前用户激活记录
 * @returns 选中的记录，无则 `undefined`
 */
function pickLatestMyLicenseActivation(list: MenuStoreMyLicenseActivation[] | undefined): MenuStoreMyLicenseActivation | undefined {
    if (list == null || list.length === 0) {
        return undefined;
    }
    /** 带可解析激活时间的记录 */
    const withTime = list.filter((row) => {
        if (row?.activated_at == null) {
            return false;
        }
        const t = new Date(row.activated_at as string).getTime();
        return !Number.isNaN(t);
    });
    if (withTime.length === 0) {
        return list[0];
    }
    return [...withTime].sort((a, b) => {
        const ta = new Date(a.activated_at as string).getTime();
        const tb = new Date(b.activated_at as string).getTime();
        return tb - ta;
    })[0];
}

/**
 * 「立即使用」弹窗：激活日期展示；优先详情 `my_license_activations`（取最新 `activated_at`），其次顶层 `activated_at`、`authorized_restaurants[].authorized_at`。
 * @param item - 当前详情合并后的菜单项
 * @returns 已格式化日期；无可用字段时返回 `undefined`（由弹窗展示占位符）
 */
export function resolveMenuStoreUseNowActivationDateLabel(item: MenuStoreMenuListItem): string | undefined {
    /** 详情接口：当前用户激活记录 */
    const fromMy = pickLatestMyLicenseActivation(item.my_license_activations)?.activated_at;
    if (fromMy != null && String(fromMy).trim() !== '') {
        return formatMenuStoreDate(fromMy);
    }
    /** 详情可能携带的扩展字段（列表体常省略） */
    const ext = item as MenuStoreMenuListItem & {
        /** 购买/激活时间（若后端单独投影） */
        activated_at?: string | Date;
        /** 已授权门店列表（详情可能返回） */
        authorized_restaurants?: Array<{ authorized_at?: string | Date }>;
    };
    /** 顶层激活时间 */
    const direct = ext.activated_at;
    if (direct != null && String(direct).trim() !== '') {
        return formatMenuStoreDate(direct);
    }
    /** 首条授权记录的授权时间 */
    const firstAuth = ext.authorized_restaurants?.[0]?.authorized_at;
    if (firstAuth != null && String(firstAuth).trim() !== '') {
        return formatMenuStoreDate(firstAuth);
    }
    return undefined;
}

/**
 * 「立即使用」弹窗：兑换码展示；优先详情 `my_license_activations`（与激活日期同一条「最新」记录）的 `code`，其次 `license_code`。
 * @param item - 当前详情合并后的菜单项
 * @returns 非空兑换码；无则 `undefined`
 */
export function resolveMenuStoreUseNowRedemptionCodeLabel(item: MenuStoreMenuListItem): string | undefined {
    /** 与激活日期对齐的同一条激活记录上的兑换码 */
    const fromMy = pickLatestMyLicenseActivation(item.my_license_activations)?.code?.trim();
    if (fromMy) {
        return fromMy;
    }
    /** 详情可能临时携带兑换码字段 */
    const ext = item as MenuStoreMenuListItem & { license_code?: string };
    /** 去掉首尾空格后的码 */
    const trimmed = ext.license_code?.trim();
    return trimmed || undefined;
}

/**
 * 复制文本到系统剪贴板，复制成功/失败均给出顶部提示。
 * 优先使用 `navigator.clipboard.writeText`（需安全上下文如 HTTPS）；不可用时回退到 `document.execCommand('copy')`。
 * @param text - 待复制文本
 */
export function copyToClipboard(text: string): void {
    /** 复制成功提示 */
    const notifyCopied = (): void => {
        GlobalNotification.success(i18n.t('menu_store_license_copied'));
    };
    /** 剪贴板不可用或失败时展示原文，便于用户手动复制 */
    const notifyFallback = (): void => {
        GlobalNotification.info(text);
    };

    if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
        void navigator.clipboard.writeText(text).then(notifyCopied).catch(notifyFallback);
        return;
    }

    try {
        /** 临时输入框，用于在不支持 Clipboard API 的环境下触发复制 */
        const textarea = document.createElement('textarea');
        textarea.value = text;
        textarea.setAttribute('readonly', '');
        textarea.style.position = 'fixed';
        textarea.style.left = '-9999px';
        textarea.style.top = '0';
        document.body.appendChild(textarea);
        textarea.select();
        /** 部分浏览器仅支持同步复制命令 */
        const executed = document.execCommand('copy');
        document.body.removeChild(textarea);
        if (executed) {
            notifyCopied();
        } else {
            notifyFallback();
        }
    } catch {
        notifyFallback();
    }
}
